import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import Toggle from "../../Shared/Toggle";
import DurationPicker from "../../Shared/DurationPicker";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import BookingPageFormSkeleton from "../../Shared/LoadingSkeletons/BookingPageFormSkeleton";
import {
  useBookingPage,
  useUpdateSettingsTab,
} from "../../../hooks/useBookingPage";

const BufferSettingsForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch config data
  const { data: configData, isLoading } = useBookingPage();

  // Update mutation for settings tab
  const { mutate: updateSettings, isPending: isUpdating } =
    useUpdateSettingsTab();

  const {
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      fixedLocationBufferEnabled: false,
      fixedLocationBufferDuration: 15,
      mobileBufferEnabled: false,
      mobileBufferDuration: 30,
    },
  });

  // Watch form values
  const fixedLocationBufferEnabled = watch("fixedLocationBufferEnabled");
  const mobileBufferEnabled = watch("mobileBufferEnabled");

  // Set form values when data loads
  useEffect(() => {
    if (configData?.data) {
      const formData = {
        fixedLocationBufferEnabled:
          configData.data.fixedLocationBufferEnabled || false,
        fixedLocationBufferDuration:
          configData.data.fixedLocationBufferDuration || 15,
        mobileBufferEnabled: configData.data.mobileBufferEnabled || false,
        mobileBufferDuration: configData.data.mobileBufferDuration || 30,
      };

      // Reset form and mark as pristine (not dirty)
      reset(formData, { keepDirty: false });
    }
  }, [configData, reset]);

  // Toggle change handlers
  const handleFixedLocationBufferToggle = (checked) => {
    setValue("fixedLocationBufferEnabled", checked, { shouldDirty: true });
    setErrors({});
    if (!isSaving) {
      setSuccess(false);
    }
  };

  const handleMobileBufferToggle = (checked) => {
    setValue("mobileBufferEnabled", checked, { shouldDirty: true });
    setErrors({});
    if (!isSaving) {
      setSuccess(false);
    }
  };

  // Memoized default duration values to prevent infinite re-renders
  const mobileDefaultDuration = useMemo(() => 
    configData?.data?.mobileBufferDuration || 30, 
    [configData?.data?.mobileBufferDuration]
  );
  
  const fixedLocationDefaultDuration = useMemo(() => 
    configData?.data?.fixedLocationBufferDuration || 15, 
    [configData?.data?.fixedLocationBufferDuration]
  );

  // Duration change handlers - memoized to prevent infinite re-renders
  const handleFixedLocationBufferDuration = useCallback((duration) => {
    setValue("fixedLocationBufferDuration", duration, { shouldDirty: true });
    setErrors({});
    // Don't clear success state for duration changes to prevent interference
  }, [setValue]);

  const handleMobileBufferDuration = useCallback((duration) => {
    setValue("mobileBufferDuration", duration, { shouldDirty: true });
    setErrors({});
    // Don't clear success state for duration changes to prevent interference
  }, [setValue]);

  const onSubmit = async (data) => {
    await performSave(data);
  };

  const performSave = async (data) => {
    setErrors({});
    setSuccess(false);
    setIsSaving(true);

    updateSettings(data, {
      onSuccess: () => {
        setSuccess(true);
        setIsSaving(false);
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error?.message;
        const errorDetails = err.response?.data?.error?.details;

        let displayMessage =
          errorMessage ||
          "Unable to save buffer time settings. Please try again.";

        // Handle specific validation errors
        if (errorDetails && Array.isArray(errorDetails)) {
          displayMessage = errorDetails.join(", ");
        }

        setErrors({
          title: "Error",
          message: displayMessage,
        });
        setIsSaving(false);
      },
    });
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <BookingPageFormSkeleton showToggle={true} inputCount={2} />
      </TabFormWrapper>
    );
  }

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        {/* Mobile Buffer */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-ink-900">
                Mobile Service Buffer Time
              </h3>
              <p className="text-sm text-ink-500">
                Add time between mobile appointments for travel and setup
              </p>
            </div>
            <Toggle
              checked={mobileBufferEnabled}
              onChange={handleMobileBufferToggle}
            />
          </div>

          {mobileBufferEnabled && (
            <div className="max-w-xs">
              <DurationPicker
                defaultDuration={mobileDefaultDuration}
                onDurationUpdatedHandler={handleMobileBufferDuration}
              />
            </div>
          )}
        </div>

        {/* Fixed Location Buffer */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-ink-900">
                Fixed Location Buffer Time
              </h3>
              <p className="text-sm text-ink-500">
                Add time between appointments at your business location
              </p>
            </div>
            <Toggle
              checked={fixedLocationBufferEnabled}
              onChange={handleFixedLocationBufferToggle}
            />
          </div>

          {fixedLocationBufferEnabled && (
            <div className="max-w-xs">
              <DurationPicker
                defaultDuration={fixedLocationDefaultDuration}
                onDurationUpdatedHandler={handleFixedLocationBufferDuration}
              />
            </div>
          )}
        </div>
      </div>
      <FormFooter
        primaryCaption="Save"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />

      {/* Alerts */}
      {errors.title && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertRed title={errors.title} message={errors.message} />
        </div>
      )}

      {success && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertGreen
            title="Success"
            message="Buffer time settings have been saved successfully"
          />
        </div>
      )}
    </TabFormWrapper>
  );
};

export default BufferSettingsForm;
